/**
 * @file LookPhotoOptions.tsx
 * @description Настройки фото фона секции (phase19.16 Р2; uiux-design-r2 §2,
 * §10). SB120 этап 0 — затемнение числом 0–60 % (`NumberSliderField`).
 * SB120 этап 1 (uiux-sb120-stage1 §2.3; данные db-design-sb120-stage1 §1):
 *
 * ```
 * Растворить край   [Нет][←][→][↑][↓]   Ширина растворения 5–100 % ↺ 45
 * Затемнение        ├──●──┤ [ 30 ] %
 * Текст на фото     [Тёмный набор][Светлый набор][Текст][Акцент]…[Свой]
 * Выход за край     Вверх 0–200 px · Вниз 0–200 px  [0][L 64][XL 96] · Вбок [Нет][Влево][Вправо]
 * ```
 */

import { SITE_THEME_LIMITS, type SectionMediaBg } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { Segmented } from "../../../site-view/Segmented";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { LookWarningNote } from "../fields/LookWarningNote";
import { useSectionForm } from "../section-form-context";
import { PhotoTextColorField } from "./PhotoTextColorField";
import { ownValue } from "../../../../lib/site-fields/number-input";
import { DIM_FIELD } from "../../../../lib/site-view/page-bg";
import { MEDIA_BG_DIM } from "../../../../lib/site-builder/section-edit";
import { spacePresets } from "../../../../lib/site-builder/look-space";
import { LOW_CONTRAST_TEXT, coverLowContrast } from "../../../../lib/site-view/cover-layout";

const FADE = [
  { value: "none" as const, label: "Нет" },
  { value: "left" as const, label: "←", ariaLabel: "Растворить левый край" },
  { value: "right" as const, label: "→", ariaLabel: "Растворить правый край" },
  { value: "top" as const, label: "↑", ariaLabel: "Растворить верхний край" },
  { value: "bottom" as const, label: "↓", ariaLabel: "Растворить нижний край" },
];

const SIDE = [
  { value: "none" as const, label: "Нет" },
  { value: "left" as const, label: "Влево" },
  { value: "right" as const, label: "Вправо" },
];

/** Ширина растворения по умолчанию (section.css: 45 %). */
const FADE_WIDTH = 45;

export function LookPhotoOptions({ bg, onChange }: { bg: SectionMediaBg; onChange: (bg: SectionMediaBg) => void }) {
  const { preview, theme } = useSectionForm();
  const density = theme?.spacing.density ?? SITE_THEME_LIMITS.density.default;
  const set = (patch: Partial<SectionMediaBg>) => onChange({ ...bg, ...patch });
  // Светлый текст без затемнения — риск, что он сольётся со светлым фото.
  const risky = !bg.textColor && coverLowContrast("background", bg.textTone, bg.dim);
  const bleedField = (edge: "top" | "bottom", label: string) => (
    <NumberSliderField
      label={label}
      value={ownValue(bg.bleed[edge], 0)}
      inherit={{ value: 0, label: "нет" }}
      min={0}
      max={200}
      step={1}
      unit="px"
      display={{ factor: 1, decimals: 2 }}
      presets={spacePresets(density, ["0", "l", "xl"])}
      onChange={(v) => set({ bleed: { ...bg.bleed, [edge]: v ?? 0 } })}
      onPreview={(v) => preview(`look.bg.bleed.${edge}`, v ?? 0)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>Растворить край</Label>
        <Segmented aria-label="Растворить край фото" options={FADE} value={bg.fade} onChange={(fade) => set({ fade })} />
        {bg.fade !== "none" ? (
          <NumberSliderField
            label="Ширина растворения"
            value={bg.fadeWidth}
            inherit={{ value: FADE_WIDTH, label: `${FADE_WIDTH} %` }}
            min={5}
            max={100}
            step={1}
            unit="%"
            onChange={(fadeWidth) => {
              const next = { ...bg, fadeWidth };
              if (fadeWidth === undefined) delete next.fadeWidth;
              onChange(next);
            }}
            onPreview={(v) => preview("look.bg.fadeWidth", v)}
          />
        ) : null}
      </div>
      {/* Умолчание — затемнение нового фото-фона (newMediaBg), «↺» возвращает его. */}
      <NumberSliderField
        label="Затемнение"
        {...DIM_FIELD}
        value={ownValue(bg.dim, MEDIA_BG_DIM)}
        inherit={{ value: MEDIA_BG_DIM, label: "по умолчанию" }}
        onChange={(dim) => set({ dim: dim ?? MEDIA_BG_DIM })}
        onPreview={(v) => preview("look.bg.dim", v ?? MEDIA_BG_DIM)}
      />
      <PhotoTextColorField bg={bg} onChange={onChange} />
      <LookWarningNote paths={["look.bg.textColor"]} />
      {risky ? <p className="type-caption rounded-md border-l-4 border-warning bg-warning/10 px-2 py-1 text-on-surface">{LOW_CONTRAST_TEXT}</p> : null}
      <div className="flex flex-col gap-2">
        <Label>Выход за край</Label>
        <p className="type-caption text-on-surface-variant">Фото заходит на соседний блок. Работает только рядом с новыми блоками.</p>
        {bleedField("top", "Вверх")}
        {bleedField("bottom", "Вниз")}
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-caption w-12 text-on-surface-variant">Вбок</span>
          <Segmented aria-label="Выход фото вбок" options={SIDE} value={bg.bleed.side} onChange={(side) => set({ bleed: { ...bg.bleed, side } })} />
        </div>
      </div>
    </div>
  );
}
